import { isItemGroupHidden } from './productGroupHours';

/**
 * Tiffin of the Day (Business Rule #58) — client helpers.
 *
 * `allocateTiffinUpsells` is the mirror of backend
 * `services/tiffin_plan.allocate_upsells`: the same units and savings, so the
 * Cart and Checkout show exactly what `create_order` charges (Business Rule #1).
 * Change one, change the other — the parity fixtures live in
 * `tiffinPlan.test.js` and `backend/tests/test_tiffin_plan.py`.
 */

const IST_OFFSET_MS = 330 * 60 * 1000;

/**
 * Today's date in IST as `YYYY-MM-DD` (Business Rule #30).
 * @param {Date} [now]
 * @returns {string}
 */
export function todayIST(now = new Date()) {
  return new Date(now.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * Is this menu row a tiffin stamped for an earlier day (a list cached past
 * midnight)? The server only stamps `tiffin_day` on today's tiffins.
 * @param {{tiffin_day?: string}} item
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isStaleTiffin(item, now = new Date()) {
  return !!item?.tiffin_day && item.tiffin_day !== todayIST(now);
}

/**
 * Is this menu row a tiffin that can't be ordered today — flagged by the server
 * (`tiffin_not_today`, from `/menu/items/batch` and `/menu/{id}`) or stale on the
 * device?
 * @param {object} item
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isTiffinUnavailableToday(item, now = new Date()) {
  return !!item?.tiffin_not_today || isStaleTiffin(item, now);
}

const sku = (v) => String(v || '').trim().toUpperCase();
const isNonVeg = (tags) => (tags || []).includes('Non Veg');

/** Size-aware base price — the chosen size's `base_price`, else the row price. */
function sizeBasePrice(menuItem, size) {
  const base = Number(menuItem?.price || 0);
  if (size) {
    const s = (menuItem?.customization_options?.sizes || []).find(x => x?.name === size);
    if (s) return Number(s.base_price != null ? s.base_price : base) || base;
  }
  return base;
}

const round2 = (x) => Math.round(x * 100) / 100;

/**
 * Which cart units get today's tiffin upsell price, and the saving.
 *
 * For each pick with an upsell, in plan order: the allowance is the number of
 * units of that tiffin in the cart. Lines of the upsell product in the upsell
 * size (not a daily-offer or free-coffee line), in cart order, take
 * min(free units, remaining allowance) units at the special price; the saving
 * is (size base − price) × units, nothing when the price isn't below the base.
 *
 * @param {Array} cartItems - cart lines `{menu_item_id, quantity, customization, daily_offer_id, is_free_coffee}`
 * @param {Object<string, object>} menuMap - `{id: menu row}` with `parent_sku`, `price`, `customization_options`
 * @param {Array} picks - today's picks `{parent_sku, upsell: {parent_sku, size, price} | null}`
 * @param {Object<number, number>} [excludedUnits] - `{cart index: units}` a bundle pass already covers
 * `openPicks` (client-only, for the Cart nudge) lists each pick whose tiffin is
 * in the cart but whose allowance isn't used up: `[{pick, missing}]`.
 * @returns {{lines: Object<number, {units: number, saving: number, price: number, base: number}>, total: number, openPicks: Array<{pick: object, missing: number}>}}
 */
export function allocateTiffinUpsells(cartItems, menuMap, picks, excludedUnits = {}) {
  const lines = cartItems || [];
  const tiffinUnits = {};
  for (const line of lines) {
    if (line?.is_free_coffee) continue;
    const p = sku(menuMap?.[line?.menu_item_id]?.parent_sku);
    if (p) tiffinUnits[p] = (tiffinUnits[p] || 0) + (parseInt(line.quantity, 10) || 0);
  }

  const used = {};
  const out = {};
  const openPicks = [];
  let total = 0;
  for (const pick of picks || []) {
    const upsell = pick?.upsell;
    if (!upsell) continue;
    let remaining = tiffinUnits[sku(pick.parent_sku)] || 0;
    const allowance = remaining;
    const target = sku(upsell.parent_sku);
    const price = Number(upsell.price || 0);
    for (let idx = 0; idx < lines.length; idx += 1) {
      if (remaining <= 0) break;
      const line = lines[idx];
      if (line.daily_offer_id || line.is_free_coffee) continue;
      const menuItem = menuMap?.[line.menu_item_id];
      if (!menuItem || sku(menuItem.parent_sku) !== target) continue;
      const size = (line.customization && !Array.isArray(line.customization) && line.customization.size) || null;
      if ((upsell.size || null) !== size) continue;
      const base = sizeBasePrice(menuItem, size);
      if (base <= price) continue;
      const free = (parseInt(line.quantity, 10) || 0) - (excludedUnits[idx] || 0) - (used[idx] || 0);
      const units = Math.min(free, remaining);
      if (units <= 0) continue;
      used[idx] = (used[idx] || 0) + units;
      remaining -= units;
      const saving = round2((base - price) * units);
      const entry = out[idx] || (out[idx] = { units: 0, saving: 0, price, base });
      entry.units += units;
      entry.saving = round2(entry.saving + saving);
      total += saving;
    }
    if (allowance > 0 && remaining > 0) openPicks.push({ pick, missing: remaining });
  }
  return { lines: out, total: round2(total), openPicks };
}

/**
 * Units a bundle pass redeems per cart index — each redemption is one unit
 * (Business Rule #23). Feeds `allocateTiffinUpsells`' `excludedUnits`.
 * @param {number[]} redeemIndices
 * @returns {Object<number, number>}
 */
export function passUnitsByIndex(redeemIndices) {
  const m = {};
  for (const idx of redeemIndices || []) m[idx] = (m[idx] || 0) + 1;
  return m;
}

/**
 * The cart customization spec for a quick-added upsell / tiffin — the same
 * `{size, extras}` shape ProductDetail writes, so the line merges with one the
 * customer adds by hand.
 * @param {{size?: string|null, has_customizations?: boolean}} entry
 * @returns {{size: string|null, extras: string[]}|null}
 */
export function quickAddSpec(entry) {
  if (!entry?.has_customizations && !entry?.size) return null;
  return { size: entry.size || null, extras: [] };
}

/**
 * Pure: today's picks the home bar may show — every pick whose tiffin is on
 * the store's live menu list (so sold-out / not-served items drop out), not
 * hidden by the partner kitchen's hours (Business Rule #52) and, in veg mode,
 * vegetarian.
 * @param {Array} picks - from GET /tiffins/today
 * @param {Array} menuItems - the store's GET /menu list (carries `serving_group`)
 * @param {{groups: Array, now: object, storeOpen: boolean, vegMode: boolean}} ctx
 * @returns {Array}
 */
export function visibleTiffinPicks(picks, menuItems, { groups = [], now, storeOpen = true, vegMode = false } = {}) {
  const byId = new Map((menuItems || []).map(m => [m.id, m]));
  return (picks || []).filter((p) => {
    const row = byId.get(p.menu_item_id);
    if (!row || row.available === false || isStaleTiffin(row)) return false;
    if (groups.length && isItemGroupHidden(row, groups, now, storeOpen)) return false;
    if (vegMode && isNonVeg(p.tags)) return false;
    return true;
  });
}

/**
 * Wording for a cart blocked by unavailable lines (Business Rule #27 / #58).
 * When EVERY blocked line is a tiffin off today's plan it says so; a mix says
 * "unavailable"; plain sold-out keeps the existing wording.
 * @param {Array<{name: string, tiffin?: boolean}>} lines - the blocked lines
 * @returns {{title: string, button: string, buttonShort: string, checkoutButton: string, toast: string}}
 */
export function blockedLinesCopy(lines) {
  const list = lines || [];
  const n = list.length;
  const tiffins = list.filter(l => l.tiffin).length;
  if (n > 0 && tiffins === n) {
    const one = n === 1;
    return {
      title: one ? "1 tiffin isn't on today's menu" : `${n} tiffins aren't on today's menu`,
      button: one ? 'Remove the tiffin to continue' : 'Remove the tiffins to continue',
      buttonShort: one ? 'Remove the tiffin' : 'Remove the tiffins',
      checkoutButton: one ? 'Remove the tiffin in cart' : 'Remove the tiffins in cart',
      toast: "Please remove tiffins that aren't on today's menu.",
    };
  }
  const title = n === 1 ? '1 item is no longer available' : `${n} items are no longer available`;
  if (tiffins > 0) {
    return {
      title,
      button: 'Remove unavailable items to continue',
      buttonShort: 'Remove unavailable items',
      checkoutButton: 'Remove unavailable items in cart',
      toast: 'Please remove unavailable items before continuing.',
    };
  }
  return {
    title,
    button: 'Remove sold-out items to continue',
    buttonShort: 'Remove sold-out items',
    checkoutButton: 'Remove sold-out items in cart',
    toast: 'Please remove sold-out items before continuing.',
  };
}
